import React, { useState, useEffect } from 'react';
import { FileText, X, ChevronRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { useQuickQuote } from '../context/QuickQuoteContext';
import { useLocation } from 'react-router-dom';

const QuickQuoteButton = () => {
  const { isDark } = useTheme();
  const { selectedProduct, openQuoteModal, clearProduct } = useQuickQuote();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const location = useLocation();

  // Show button only on product-related pages
  const isProductPage = location.pathname.includes('/products');

  // Auto-expand when product is selected
  useEffect(() => {
    if (selectedProduct) {
      setIsExpanded(true);
      setIsVisible(true);
    }
  }, [selectedProduct]);

  // Hide on non-product pages
  useEffect(() => {
    if (!isProductPage) {
      setIsVisible(false);
      setIsExpanded(false);
    } else if (selectedProduct) {
      setIsVisible(true);
    }
  }, [isProductPage, selectedProduct]);

  if (!isVisible || !selectedProduct) return null;

  return (
    <div 
      className={`fixed bottom-24 right-6 z-50 transition-all duration-300 ${
        isExpanded ? 'w-80' : 'w-auto'
      }`}
      data-testid="quick-quote-button"
    >
      {isExpanded ? (
        /* Expanded Card */
        <div className={`rounded-2xl shadow-2xl overflow-hidden ${
          isDark ? 'bg-[#0A0A0A] border border-white/10' : 'bg-white border border-black/10'
        }`}>
          {/* Header */}
          <div className={`p-4 border-b ${isDark ? 'border-white/10' : 'border-black/5'}`}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs tracking-[0.15em] uppercase text-amber-500 font-medium">
                Quick Quote
              </span>
              <button
                onClick={() => {
                  setIsExpanded(false);
                  setTimeout(() => {
                    clearProduct();
                    setIsVisible(false);
                  }, 300);
                }}
                className={`w-6 h-6 rounded-full flex items-center justify-center transition-colors ${
                  isDark ? 'hover:bg-white/10 text-white/40' : 'hover:bg-black/5 text-black/40'
                }`}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className={`text-sm ${isDark ? 'text-white/60' : 'text-black/60'}`}>
              Get pricing for this product
            </p>
          </div>

          {/* Product Preview */}
          <div className="p-4">
            <div className="flex gap-3 items-start">
              {/* Thumbnail */}
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-black/5 shrink-0">
                <img 
                  src={selectedProduct.featured_image || selectedProduct.image || '/assets/images/placeholder.jpg'}
                  alt={selectedProduct.name}
                  className="w-full h-full object-cover"
                />
              </div>
              
              {/* Details */}
              <div className="flex-1 min-w-0">
                <h4 className={`text-sm font-medium mb-1 line-clamp-2 ${isDark ? 'text-white' : 'text-black'}`}>
                  {selectedProduct.name}
                </h4>
                <p className={`text-xs ${isDark ? 'text-white/40' : 'text-black/40'}`}>
                  {selectedProduct.category?.replace(/-/g, ' ')}
                </p>
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={() => openQuoteModal(selectedProduct)}
              className="w-full mt-4 h-12 bg-amber-500 hover:bg-amber-400 text-black font-medium rounded-lg flex items-center justify-center gap-2 transition-all duration-300 group"
            >
              <FileText className="w-4 h-4" />
              Request Quote
              <ChevronRight className="w-4 h-4 opacity-0 -ml-2 group-hover:opacity-100 group-hover:ml-0 transition-all" />
            </button>
          </div>
        </div>
      ) : (
        /* Collapsed Button */
        <button
          onClick={() => setIsExpanded(true)}
          className="h-14 px-5 bg-amber-500 hover:bg-amber-400 text-black font-medium rounded-full flex items-center gap-2 shadow-lg hover:shadow-xl transition-all duration-300 group"
        >
          <FileText className="w-5 h-5" />
          <span className="text-sm">Quick Quote</span>
          <div className="w-5 h-5 rounded-full bg-black/10 flex items-center justify-center text-xs font-bold">
            1
          </div>
        </button>
      )}
    </div>
  );
};

export default QuickQuoteButton;
